import { useMemo } from 'react';

import { useMutation } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import { Button, CircularProgress, Stack } from '@mui/material';
import { SaveIcon } from 'lucide-react';
import { type DeepPartial, useFormContext } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type {
  Lots_Set_Input,
  MarketingFormDataFragment,
} from '@graphql/__generated__/graphql';
import {
  type FormDefinitionType,
  RaForm,
  createCategoryElement,
  createSelectElement,
  createTextFieldElement,
} from '@modules/_shared/components/form/RaForm';
import {
  type IntlLocale,
  type Translate,
  useLocale,
} from '@shared/hooks/locale';

export const MARKETING_FORM_DATA_FRAGMENT = gql(/* GraphQL */ `
  fragment MarketingFormData on lots {
    id
    dummy_living_surface
    dummy_usable_surface
    listing_url
    property {
      id
      gross_floor_surface
      living_surface
      usable_surface
      weighted_floor_surface
      built_surface
    }
    tenant {
      id
      country_code
    }
  }
`);

const UPDATE_LISTING = gql(/* GraphQL */ `
  mutation updateListingMarketingForm($id: uuid!, $lot: lots_set_input) {
    update_lots_by_pk(pk_columns: { id: $id }, _set: $lot) {
      id
      ...MarketingFormData
    }
  }
`);

type MarketingFormProps = {
  listing: MarketingFormDataFragment;
  readOnly?: boolean;
};

type FormData = Pick<
  Lots_Set_Input,
  'dummy_living_surface' | 'dummy_usable_surface' | 'listing_url'
>;

const formatArea = (
  value: null | number | undefined,
  t: Translate,
  locale: IntlLocale,
) => {
  return value == null ? t('None') : `${value.toLocaleString(locale)} m²`;
};

const FormActionButtons: React.FC<{
  defaultValues: DeepPartial<FormData>;
  readOnly?: boolean;
}> = ({ defaultValues, readOnly = false }) => {
  const { t } = useLocale();
  const {
    reset,
    formState: { isSubmitting, isDirty },
  } = useFormContext();

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        mt: 3,
        justifyContent: 'flex-end',
      }}
    >
      <Button
        variant="outlined"
        color="neutral"
        disabled={isSubmitting}
        onClick={() => {
          reset(defaultValues);
        }}
      >
        {t('Cancel')}
      </Button>
      <LoadingButton
        type="submit"
        variant="contained"
        color="primary"
        loadingIndicator={
          <CircularProgress disableShrink color="inherit" size={16} />
        }
        disabled={readOnly || !isDirty}
        loading={isSubmitting}
        loadingPosition="start"
        startIcon={<SaveIcon />}
      >
        {t('Save')}
      </LoadingButton>
    </Stack>
  );
};

export const MarketingForm: React.FC<MarketingFormProps> = ({
  listing,
  readOnly = false,
}) => {
  const { locale, t } = useLocale();
  const [updateListing] = useMutation(UPDATE_LISTING);
  const isTenantSpanish = listing.tenant.country_code === 'ES';

  const livingSurfaceOptions = useMemo(
    () => [
      {
        value: 'gross_surface',
        label: t('Gross surface - {{surface}}', {
          surface: formatArea(listing.property.gross_floor_surface, t, locale),
        }),
      },
      isTenantSpanish
        ? {
            value: 'built_surface',
            label: t('Built surface - {{surface}}', {
              surface: formatArea(listing.property.built_surface, t, locale),
            }),
          }
        : {
            value: 'living_surface',
            label: t('Living surface - {{surface}}', {
              surface: formatArea(listing.property.living_surface, t, locale),
            }),
          },
      {
        value: 'usable_surface',
        label: t('Usable surface - {{surface}}', {
          surface: formatArea(listing.property.usable_surface, t, locale),
        }),
      },
      {
        value: 'weighted_surface',
        label: t('Weighted surface - {{surface}}', {
          surface: formatArea(
            listing.property.weighted_floor_surface,
            t,
            locale,
          ),
        }),
      },
      { value: 'unpublished', label: t('Hidden') },
    ],
    [listing, t, locale, isTenantSpanish],
  );

  const formDefinition = useMemo<FormDefinitionType<FormData>>(
    () => () => {
      return [
        createCategoryElement('listing_surfaces_options', t('Listing options')),
        createTextFieldElement('listing_url', t('Listing custom URL'), {
          gridProps: { xs: 12, md: 12 },
        }),
        createSelectElement(
          'dummy_living_surface',
          isTenantSpanish ? t('Built surface') : t('Living surface'),
          () => livingSurfaceOptions,
        ),
        createSelectElement(
          'dummy_usable_surface',
          t('Usable surface'),
          () => livingSurfaceOptions,
        ),
      ];
    },
    [livingSurfaceOptions, t, isTenantSpanish],
  );

  const defaultValues = useMemo<DeepPartial<FormData>>(
    () => ({
      dummy_living_surface: listing.dummy_living_surface,
      dummy_usable_surface: listing.dummy_usable_surface,
      listing_url: listing.listing_url,
    }),
    [listing],
  );

  return (
    <RaForm
      formDefinition={formDefinition}
      defaultValues={defaultValues}
      readOnly={readOnly}
      gridProps={{ sx: { padding: 0 } }}
      actionButtonsComponent={
        <FormActionButtons defaultValues={defaultValues} readOnly={readOnly} />
      }
      onSubmit={data =>
        updateListing({
          variables: {
            id: listing.id,
            lot: {
              dummy_living_surface: data.dummy_living_surface,
              dummy_usable_surface: data.dummy_usable_surface,
              listing_url: data.listing_url,
            },
          },
        })
      }
    />
  );
};
